import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ArrowLeft } from "lucide-react";
import api from "../Helper/api"; // Ortak axios instance
import { openProtectedFile } from "../Helper/openProtectedFile";

export default function CreateExpensePage({ onCancel, onSuccess }) {
  const navigate = useNavigate();
  const auth = useSelector((state) => state.auth.loginUser);

  const [fullName, setFullName] = useState("");
  const [iban, setIban] = useState("");

  // Harcama Kalemi Ekleme Formu State'leri
  const [documentNo, setDocumentNo] = useState("");
  const [expenseDate, setExpenseDate] = useState("");
  const [expenseType, setExpenseType] = useState("");
  const [description, setDescription] = useState("");
  const [documentType, setDocumentType] = useState("Fiş");
  const [amount, setAmount] = useState("");
  const [uploadingFile, setUploadingFile] = useState(false);
  const [documentUrl, setDocumentUrl] = useState("");

  // Geçici Harcama Kalemleri Listesi
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savedExpense, setSavedExpense] = useState(null);

  useEffect(() => {
    if (auth) {
      setFullName(`${auth.name || ""} ${auth.surname || ""}`.trim());
    }
  }, [auth]);

  const getTodayValue = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const todayValue = getTodayValue();

  const handleIbanChange = (e) => {
    const value = e.target.value;
    const onlyDigits = value.replace(/\D/g, "");
    setIban(onlyDigits.slice(0, 24));
  };

  const handleAddExpenseItem = (e) => {
    e.preventDefault();

    if (!documentNo.trim()) {
      toast.error("Belge No zorunludur.");
      return;
    }
    if (!expenseDate) {
      toast.error("Harcama Tarihi zorunludur.");
      return;
    }
    if (expenseDate > todayValue) {
      toast.error("Bugünden sonraki bir tarih seçilemez.");
      return;
    }
    if (!expenseType) {
      toast.error("Harcama Türü seçilmelidir.");
      return;
    }
    if (!amount || Number(amount) <= 0) {
      toast.error("Tutar 0'dan büyük olmalıdır.");
      return;
    }

    const newItem = {
      documentNo: documentNo.trim(),
      expenseDate,
      expenseType,
      description: description.trim(),
      documentType,
      amount: Number(amount),
      documentUrl: documentUrl || "",
    };

    setItems((prev) => [...prev, newItem]);

    // Temizle
    setDocumentNo("");
    setExpenseDate("");
    setExpenseType("");
    setDescription("");
    setDocumentType("Fiş");
    setAmount("");
    setDocumentUrl("");
  };

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    const allowedTypes = ["application/pdf", "image/png", "image/jpeg", "image/jpg"];
    if (!allowedTypes.includes(selectedFile.type)) {
      toast.error("Yalnızca PDF, PNG veya JPEG/JPG dosyaları yükleyebilirsiniz.");
      e.target.value = null;
      return;
    }

    setUploadingFile(true);
    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      const response = await api.post("/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      if (response.status === 200) {
        setDocumentUrl(response.data.fileUrl);
        toast.success("Dosya başarıyla yüklendi.");
      }
    } catch (error) {
      console.error("Dosya yükleme hatası:", error);
      toast.error("Dosya yüklenirken bir hata oluştu.");
      e.target.value = null;
    } finally {
      setUploadingFile(false);
    }
  };

  const handleRemoveItem = (indexToRemove) => {
    setItems((prev) => prev.filter((_, i) => i !== indexToRemove));
  };

  const totalAmount = items.reduce((sum, item) => sum + item.amount, 0);

  const handleSubmitForm = async () => {
    if (!fullName.trim()) {
      toast.error("Ad Soyad zorunludur.");
      return;
    }
    if (iban.length !== 24) {
      toast.error("IBAN 24 haneli olmalıdır.");
      return;
    }
    if (items.length === 0) {
      toast.error("En az bir harcama kalemi eklemelisiniz.");
      return;
    }

    setLoading(true);

    try {
      const response = await api.post("/expenses", {
        fullName,
        iban,
        items,
      });

      if (response.status === 201) {
        toast.success("Masraf formu başarıyla kaydedildi.");
        setSavedExpense(response.data.expense);
      } else {
        toast.error("Form kaydedilemedi.");
      }
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "Masraf kaydedilemedi.");
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className={onCancel ? "w-full print:p-0" : "max-w-4xl mx-auto p-6 space-y-6 print:p-0 print:max-w-full"}>
      {/* Yazdırma Modunda Gizlenecek Form Arayüzü */}
      <div className="print:hidden space-y-4">
        {!onCancel && (
          <button
            onClick={() => navigate("/masraflar")}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors font-medium text-sm mb-2"
          >
            <ArrowLeft size={16} />
            Masraflarıma Geri Dön
          </button>
        )}

        <div className={`flex flex-wrap justify-between items-center gap-3 ${onCancel ? "pr-12" : ""}`}>
          <h1 className="text-2xl font-semibold text-gray-800">Yeni Masraf Talebi</h1>
          {savedExpense && (
            <div className="flex items-center gap-2">
              <button
                onClick={onSuccess ? onSuccess : () => navigate("/masraflar")}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm"
              >
                Masraflarıma Git
              </button>
              <button
                onClick={handlePrint}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm"
              >
                Yazdır (A4 Çıktı)
              </button>
            </div>
          )}
        </div>

        <div className="bg-white p-6 rounded-lg shadow space-y-4">
          <h2 className="text-lg font-medium text-gray-700 border-b pb-2">Personel Bilgileri</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="expense-fullname" className="block text-sm font-semibold text-gray-700 mb-1">Ad Soyad</label>
              <input
                id="expense-fullname"
                name="fullName"
                autoComplete="name"
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Ad Soyad"
                className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-blue-400"
                disabled={items.length > 0}
              />
            </div>
            <div>
              <label htmlFor="expense-iban" className="block text-sm font-semibold text-gray-700 mb-1">IBAN (TR olmadan 24 Hane)</label>
              <div className="relative flex items-center">
                <span className="absolute left-3 text-gray-500 font-semibold">TR</span>
                <input
                  id="expense-iban"
                  name="iban"
                  type="text"
                  value={iban}
                  onChange={handleIbanChange}
                  placeholder="000000000000000000000000"
                  className="w-full border rounded pl-10 pr-3 py-2 focus:ring-2 focus:ring-blue-400"
                  disabled={items.length > 0}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">Girelen hane: {iban.length}/24</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleAddExpenseItem} className="bg-white p-6 rounded-lg shadow space-y-4">
          <h2 className="text-lg font-medium text-gray-700 border-b pb-2">Harcama Ekle</h2>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label htmlFor="expense-doc-no" className="block text-sm font-semibold text-gray-700 mb-1">Belge (Fiş/Fatura) No</label>
              <input
                id="expense-doc-no"
                name="documentNo"
                type="text"
                value={documentNo}
                onChange={(e) => setDocumentNo(e.target.value.replace(/\D/g, ""))}
                placeholder="Rakam giriniz"
                className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-blue-400"
              />
            </div>
            <div>
              <label htmlFor="expense-date" className="block text-sm font-semibold text-gray-700 mb-1">Harcama Tarihi</label>
              <input
                id="expense-date"
                name="expenseDate"
                type="date"
                value={expenseDate}
                onChange={(e) => setExpenseDate(e.target.value)}
                max={todayValue}
                className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-blue-400"
              />
            </div>
            <div>
              <label htmlFor="expense-doc-type" className="block text-sm font-semibold text-gray-700 mb-1">Belge Tipi</label>
              <select
                id="expense-doc-type"
                name="documentType"
                value={documentType}
                onChange={(e) => setDocumentType(e.target.value)}
                className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-blue-400"
              >
                <option value="Fiş">Fiş</option>
                <option value="Fatura">Fatura</option>
                <option value="E-Fatura">E-Fatura</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label htmlFor="expense-type" className="block text-sm font-semibold text-gray-700 mb-1">Harcama Türü</label>
              <select
                id="expense-type"
                name="expenseType"
                value={expenseType}
                onChange={(e) => setExpenseType(e.target.value)}
                className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-blue-400"
              >
                <option value="">Seçiniz...</option>
                <option value="Yol/Ulaşım">Yol/Ulaşım</option>
                <option value="Yemek/Temsil">Yemek/Temsil</option>
                <option value="Konaklama">Konaklama</option>
                <option value="Ofis Gideri">Ofis Gideri</option>
                <option value="Diğer">Diğer</option>
              </select>
            </div>
            <div className="col-span-2">
              <label htmlFor="expense-description" className="block text-sm font-semibold text-gray-700 mb-1">Açıklama (Opsiyonel)</label>
              <input
                id="expense-description"
                name="description"
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Açıklama yazınız"
                className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-blue-400"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 items-end">
            <div>
              <label htmlFor="expense-amount" className="block text-sm font-semibold text-gray-700 mb-1">Tutar (TL)</label>
              <input
                id="expense-amount"
                name="amount"
                type="number"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-blue-400"
              />
            </div>
            <div className="col-span-2">
              <label htmlFor="expense-file" className="block text-sm font-semibold text-gray-700 mb-1">Belge Fotoğrafı / PDF (Opsiyonel)</label>
              <input
                id="expense-file"
                name="file"
                type="file"
                accept="image/*,application/pdf"
                onChange={handleFileChange}
                className="w-full border rounded px-3 py-1.5 focus:ring-2 focus:ring-blue-400 text-sm file:mr-4 file:py-1 file:px-2.5 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              {uploadingFile && <p className="text-xs text-blue-500 mt-1">Yükleniyor...</p>}
              {documentUrl && (
                <p className="text-xs text-green-600 mt-1">✓ Belge yüklendi</p>
              )}
            </div>
          </div>

          <div className="flex justify-end pt-2">
            <button
              type="submit"
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md font-medium transition-colors"
              disabled={uploadingFile}
            >
              Listeye Ekle
            </button>
          </div>
        </form>

        {items.length > 0 && (
          <div className="bg-white p-6 rounded-lg shadow space-y-4">
            <h2 className="text-lg font-medium text-gray-700 border-b pb-2">Eklenen Harcamalar</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse min-w-[650px]">
                <thead>
                  <tr className="border-b bg-gray-50 text-sm font-semibold text-gray-600">
                    <th className="py-2 px-3">Belge No</th>
                    <th className="py-2 px-3">Tarih</th>
                    <th className="py-2 px-3">Belge Tipi</th>
                    <th className="py-2 px-3">Tür</th>
                    <th className="py-2 px-3">Açıklama</th>
                    <th className="py-2 px-3">Belge Görseli</th>
                    <th className="py-2 px-3 text-right">Tutar</th>
                    <th className="py-2 px-3 text-center">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, index) => (
                    <tr key={index} className="border-b text-sm text-gray-700">
                      <td className="py-2 px-3">{item.documentNo}</td>
                      <td className="py-2 px-3">{new Date(item.expenseDate).toLocaleDateString("tr-TR")}</td>
                      <td className="py-2 px-3">{item.documentType}</td>
                      <td className="py-2 px-3">{item.expenseType}</td>
                      <td className="py-2 px-3">{item.description || "-"}</td>
                      <td className="py-2 px-3">
                        {item.documentUrl ? (
                          <button
                            type="button"
                            onClick={() => openProtectedFile(item.documentUrl)}
                            className="text-blue-600 hover:underline font-medium"
                          >
                            Görüntüle
                          </button>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="py-2 px-3 text-right">{item.amount.toLocaleString("tr-TR", { minimumFractionDigits: 2 })} TL</td>
                      <td className="py-2 px-3 text-center">
                        <button
                          onClick={() => handleRemoveItem(index)}
                          className="text-red-600 hover:text-red-800 font-medium"
                        >
                          Sil
                        </button>
                      </td>
                    </tr>
                  ))}
                  <tr className="font-semibold text-base text-gray-800">
                    <td colSpan="6" className="py-3 px-3 text-right">Toplam İade Tutarı:</td>
                    <td className="py-3 px-3 text-right border-t-2 border-gray-800">{totalAmount.toLocaleString("tr-TR", { minimumFractionDigits: 2 })} TL</td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>

            {!savedExpense && (
              <div className="flex justify-end pt-4 border-t">
                <button
                  onClick={handleSubmitForm}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors shadow"
                  disabled={loading}
                >
                  {loading ? "Kaydediliyor..." : "Formu Kaydet"}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* A4 YAZDIRILABİLİR ŞABLON (Sadece Yazdırma Sırasında ve savedExpense Varsa Görünür) */}
      {(savedExpense || items.length > 0) && (
        <div className="hidden print:block print-area bg-white text-black p-8 font-sans border-2 border-black max-w-[21cm] min-h-[29.7cm] mx-auto box-border">
          <div className="flex justify-between items-center border-b-2 border-black pb-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold uppercase tracking-wider text-gray-900">PAVZA BİLİŞİM</h1>
              <p className="text-xs text-gray-500">Personel Masraf Bildirim Formu</p>
            </div>
            <div className="text-right">
              <p className="text-sm font-semibold">Tarih: {new Date().toLocaleDateString("tr-TR")}</p>
              {savedExpense && <p className="text-xs font-mono text-gray-600">Form ID: {savedExpense._id || savedExpense.id}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-6 border-b pb-4">
            <div>
              <p className="text-sm"><strong>Personel Ad Soyad:</strong> {fullName}</p>
              <p className="text-sm"><strong>TC Kimlik No:</strong> {auth?.tc || savedExpense?.owner?.tc}</p>
            </div>
            <div>
              <p className="text-sm"><strong>Banka IBAN:</strong> TR{iban}</p>
              <p className="text-sm"><strong>Talep Durumu:</strong> {savedExpense ? savedExpense.status : "TASLAK"}</p>
            </div>
          </div>

          <table className="w-full text-left border-collapse border border-black mb-8">
            <thead>
              <tr className="bg-gray-100 border-b border-black text-xs font-bold uppercase text-gray-800">
                <th className="border border-black py-2 px-3">Belge No</th>
                <th className="border border-black py-2 px-3">Tarih</th>
                <th className="border border-black py-2 px-3">Tip</th>
                <th className="border border-black py-2 px-3">Tür</th>
                <th className="border border-black py-2 px-3">Açıklama</th>
                <th className="border border-black py-2 px-3">Belge Görseli</th>
                <th className="border border-black py-2 px-3 text-right">Tutar</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => (
                <tr key={index} className="border-b border-black text-sm">
                  <td className="border border-black py-2 px-3">{item.documentNo}</td>
                  <td className="border border-black py-2 px-3">{new Date(item.expenseDate).toLocaleDateString("tr-TR")}</td>
                  <td className="border border-black py-2 px-3">{item.documentType}</td>
                  <td className="border border-black py-2 px-3">{item.expenseType}</td>
                  <td className="border border-black py-2 px-3">{item.description || "-"}</td>
                  <td className="border border-black py-2 px-3">
                    {item.documentUrl ? (
                      <button type="button" onClick={() => openProtectedFile(item.documentUrl)} className="text-blue-600 underline">
                        Görüntüle
                      </button>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td className="border border-black py-2 px-3 text-right">{item.amount.toLocaleString("tr-TR", { minimumFractionDigits: 2 })} TL</td>
                </tr>
              ))}
              <tr className="font-bold text-base bg-gray-50">
                <td colSpan="6" className="border border-black py-3 px-3 text-right">Toplam Tutar:</td>
                <td className="border border-black py-3 px-3 text-right">
                  {totalAmount.toLocaleString("tr-TR", { minimumFractionDigits: 2 })} TL
                </td>
              </tr>
            </tbody>
          </table>

          {/* İmzalar */}
          <div className="grid grid-cols-2 gap-8 mt-16 text-center">
            <div>
              <p className="font-bold border-b border-black pb-2 mb-2">Talep Eden Personel</p>
              <p className="text-sm">{fullName}</p>
              <p className="text-xs text-gray-500 mt-4">(İmza)</p>
            </div>
            <div>
              <p className="font-bold border-b border-black pb-2 mb-2">Onaylayan Yönetici</p>
              <p className="text-sm">Sistem Yöneticisi</p>
              <p className="text-xs text-gray-500 mt-4">(İmza / Kaşe)</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
